import { Link } from "@tanstack/react-router";
import { Calculator, Download, FileText } from "lucide-react";
import type { IndustryLinkPlan } from "@/lib/internal-links";
import { getTemplate, templateDownload } from "@/content/templates";
import { trackEvent } from "@/lib/analytics";

interface Props {
  /** Same curated plan the "Where to go next" block uses. */
  plan: IndustryLinkPlan;
  /** Service or sector name, used so headings stay specific. */
  subject: string;
  /** Analytics source label, e.g. "service_page". */
  source?: string;
  /** Optional intro override. */
  intro?: string;
}

/**
 * Optional "Related calculators and tools" module. Renders nothing when the
 * link plan has no calculators or templates, so pages can drop it in safely.
 * Calculator clicks fire `tool_interaction`; template downloads fire
 * `template_download`.
 */
export function RelatedToolsModule({ plan, subject, source = "service_page", intro }: Props) {
  const calculators = plan.calculators ?? [];
  const templates = plan.templates ?? [];
  if (calculators.length === 0 && templates.length === 0) return null;

  return (
    <section className="border-t border-border bg-muted/30 px-6 py-20 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent">
          Calculators and tools
        </p>
        <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-brand sm:text-4xl">
          Related calculators and tools for {subject}
        </h2>
        <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">
          {intro ??
            "Run the numbers before you act, then take the paperwork that matches the decision. Every calculator uses current statutory rates and every template is drafted by our employment law team."}
        </p>

        <div className="mt-12 grid gap-10 lg:grid-cols-2">
          {calculators.length > 0 && (
            <div>
              <h3 className="font-serif text-xl leading-tight text-brand">
                Calculators for the numbers behind this
              </h3>
              <ul className="mt-5 space-y-4">
                {calculators.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      onClick={() =>
                        trackEvent("tool_interaction", {
                          category: "tools",
                          action: "open_calculator",
                          label: link.label,
                          properties: {
                            tool_slug: link.slug,
                            destination: link.path,
                            module: "related_tools",
                            source,
                            subject,
                          },
                        })
                      }
                      className="group flex items-start gap-3 rounded-md border border-border bg-card p-4 transition-colors hover:border-accent/60 hover:bg-secondary"
                    >
                      <Calculator className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden="true" />
                      <span>
                        <span className="block text-sm font-medium text-foreground group-hover:text-brand">
                          {link.label}
                          {link.meta ? (
                            <span className="ml-2 rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                              {link.meta}
                            </span>
                          ) : null}
                        </span>
                        {link.context ? (
                          <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                            {link.context}
                          </span>
                        ) : null}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {templates.length > 0 && (
            <div>
              <h3 className="font-serif text-xl leading-tight text-brand">
                Templates you can download now
              </h3>
              <ul className="mt-5 space-y-4">
                {templates.map((link) => {
                  const template = link.slug ? getTemplate(link.slug) : undefined;
                  const file = template ? templateDownload(template) : undefined;
                  return (
                    <li
                      key={link.path}
                      className="rounded-md border border-border bg-card p-4 transition-colors hover:border-accent/60"
                    >
                      <div className="flex items-start gap-3">
                        <FileText className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden="true" />
                        <div>
                          <Link
                            to={link.path}
                            onClick={() =>
                              trackEvent("tool_interaction", {
                                category: "tools",
                                action: "open_template",
                                label: link.label,
                                properties: {
                                  template_slug: link.slug,
                                  destination: link.path,
                                  module: "related_tools",
                                  source,
                                  subject,
                                },
                              })
                            }
                            className="block text-sm font-medium text-foreground hover:text-brand"
                          >
                            {link.label}
                          </Link>
                          {link.context ? (
                            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                              {link.context}
                            </p>
                          ) : null}
                          {file && template ? (
                            <Link
                              to={link.path}
                              onClick={() =>
                                trackEvent("template_download", {
                                  category: "templates",
                                  action: "open_gated_form",
                                  label: template.title,
                                  properties: {
                                    template_slug: template.slug,
                                    format: template.format,
                                    file: file.filename,
                                    module: "related_tools",
                                    source,
                                    subject,
                                  },
                                })
                              }
                              className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-accent underline-offset-4 hover:underline"
                            >
                              <Download className="size-4" aria-hidden="true" />
                              Get the {template.format} version
                            </Link>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
